// Thin client for the standard Laravel REST endpoints under /api/v1.
//
// The rest of the app talks to the Arc gateway over RPC; new endpoints are
// plain REST, so this unwraps the { operation_id, data } envelope and turns a
// non-2xx response into a thrown Error carrying the server's message.
//
// The path is relative on purpose: locally the node dev server proxies
// /api/v1 to Laravel, and in production nginx does the same.

const API_ROOT = '/api/v1';

function readCookie(name) {
    const match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[2]) : null;
}

function request(method, path, { query, body } = {}) {
    const url = new URL(API_ROOT + path, window.location.origin);

    if (query) {
        Object.keys(query).forEach((key) => {
            const value = query[key];
            if (value === undefined || value === null || value === '') return;
            [].concat(value).forEach((item) => url.searchParams.append(Array.isArray(value) ? key + '[]' : key, item));
        });
    }

    const headers = { Accept: 'application/json' };
    const xsrfToken = readCookie('XSRF-TOKEN');

    // A FormData body is multipart: the browser has to set the boundary itself.
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

    if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
    if (xsrfToken) headers['X-XSRF-TOKEN'] = xsrfToken;

    return fetch(url.toString(), {
        method,
        headers,
        credentials: 'same-origin',
        body: body === undefined ? undefined : (isForm ? body : JSON.stringify(body)),
    }).then((response) =>
        // A 204 has no body to parse.
        (response.status === 204 ? Promise.resolve(null) : response.json()).then((payload) => {
            if (!response.ok) {
                // A validation failure answers "The given data was invalid."
                // and puts the reason in `errors`. Showing the envelope leaves
                // the user with nothing to act on, so the first field message
                // wins when there is one.
                const fieldErrors = payload.errors && Object.keys(payload.errors).length
                    ? [].concat(payload.errors[Object.keys(payload.errors)[0]])
                    : [];

                const error = new Error(fieldErrors[0] || payload.message || 'Request failed');
                error.code = payload.code;
                error.status = response.status;
                error.errors = payload.errors;
                // A conflict names the records it is with.
                error.data = payload.data;
                throw error;
            }
            return payload;
        })
    );
}

export const api = {
    get: (path, query) => request('GET', path, { query }),
    post: (path, body) => request('POST', path, { body }),
    postForm: (path, formData) => request('POST', path, { body: formData }),
    put: (path, body) => request('PUT', path, { body }),
    patch: (path, body) => request('PATCH', path, { body }),
    del: (path) => request('DELETE', path),
};

/**
 * The card's owner choice as both endpoints take it. Exactly one of the three
 * ways of naming an owner travels; the server refuses a payload naming two.
 */
function ownerBody(choice) {
    if (!choice || choice.needOwner) {
        return { need_owner: true, investor_id: null, create_investor: false, true_owner_key: null };
    }

    // A Kore lead picked for the assessor's entity is the owner itself, so the
    // flag that would file the entity new does not travel with it.
    const entityAsNew = !choice.koreLeadId && choice.ownerOfRecord;

    return {
        need_owner: false,
        parcel_id: choice.parcelId,
        owner_name: choice.ownerName || undefined,
        investor_id: choice.investorId || undefined,
        create_investor: entityAsNew || undefined,
        owner_of_record: entityAsNew || undefined,
        true_owner_key: choice.trueOwnerKey || undefined,
        // The answer to the Kore duplicate check, when this save files someone.
        kore_lead_id: choice.koreLeadId || undefined,
        create_new: choice.createNew || undefined,
        contact: choice.contact || {},
        fields: choice.fields || [],
    };
}

export const propertyGroupApi = {
    search: (body) => api.post('/property-groups/search', body),
};

export const mapApi = {
    pins: (filters) => api.get('/map/pins', filters).then((res) => res.data),
    // missing = the ZIPs asked for that the server holds no shape of.
    zipBoundaries: (zips) =>
        api.post('/map/zip-boundaries', { zips }).then((res) => ({ boundaries: res.data, missing: res.meta.missing })),
    property: (id) => api.get('/map/properties/' + id).then((res) => res.data),
    parcelAt: (lat, lng) => api.get('/map/parcels/at', { lat, lng }).then((res) => res.data),
    // The whole envelope, not just data: the viewport layer needs the total to
    // know whether what came back is everything that is there.
    parcelsWithin: (wkt, limit, includeResidential) =>
        api.post('/map/parcels/within', { wkt, limit, include_residential: Boolean(includeResidential) }),
    parcelOutlines: (wkt, limit, includeResidential) =>
        api.post('/map/parcels/outlines', { wkt, limit, include_residential: Boolean(includeResidential) }),
    // One owner choice, whichever list on the card made it — see
    // ParcelMap.ownerPayload(). The server resolves a LightBox contact itself,
    // because what lands in INVESTORS and in the CRM must be what LightBox
    // says rather than what a browser posted.
    createProperty: (parcel, overrides, choice) =>
        api.post('/map/properties', { parcel, overrides, ...ownerBody(choice) }).then((res) => res.data),

    // The whole envelope: meta.has_record says whether LightBox holds a contact
    // record for the parcel at all, which is not the same as holding none.
    trueOwners: (parcelId, ownerName, refresh) =>
        api.get('/map/parcels/' + encodeURIComponent(parcelId) + '/true-owners', {
            owner_name: ownerName || '',
            refresh: refresh ? 1 : undefined,
        }),

    // The owner of a property iCLIMB already holds, from the same choice.
    setPropertyOwner: (propertyId, choice) =>
        api
            .post('/map/properties/' + propertyId + '/owner', {
                ...ownerBody(choice),
                alongside: Boolean(choice.alongside),
            })
            .then((res) => res.data),

    // The owner-research flag on a property iCLIMB holds. On a parcel nobody
    // holds the card keeps the answer in its own state until the add; there is
    // no property to carry it yet.
    setNeedOwner: (propertyId, needOwner) =>
        api
            .patch('/map/properties/' + propertyId + '/need-owner', { need_owner: Boolean(needOwner) })
            .then((res) => res.data),

    comptroller: (entityName, ownerAddress) =>
        api.get('/map/comptroller', { entity_name: entityName, owner_address: ownerAddress }).then((res) => res.data),

    searchInvestors: (term) => api.get('/map/investors', { search: term, per_page: 10 }).then((res) => res.data),

    // The Kore leads each owner about to be filed may already be. An owner is
    // { parcel, true_owner_key?, owner_of_record? }, named the way the save names it.
    leadMatches: (owners) => api.post('/map/parcels/lead-matches', { owners }).then((res) => res.data),

    // decisions: [{ parcel_id, kore_lead_id } | { parcel_id, create_new: true }]
    createProperties: (parcels, decisions) =>
        api.post('/map/properties/bulk', { parcels, decisions: decisions || [] }).then((res) => res.data),
    deleteProperty: (id) => api.del('/map/properties/' + id).then((res) => res.data),
    parcelDiff: (id) => api.get('/map/properties/' + id + '/parcel-diff').then((res) => res.data),
    applyParcel: (id, fields) => api.post('/map/properties/' + id + '/apply-parcel', { fields }).then((res) => res.data),
    applyParcelBulk: (ids) => api.post('/map/properties/apply-parcel', { ids }).then((res) => res.data),

    imports: () => api.get('/map/parcel-imports').then((res) => res.data),
    startImport: (zip, includeResidential) =>
        api.post('/map/parcel-imports', { zip, include_residential: Boolean(includeResidential) }).then((res) => res.data),
    // The envelope again: the queue pages by cursor, and meta carries both the
    // full count and where to resume from. kore narrows it to one tab.
    importRows: (id, kind, cursor, kore) =>
        api.get('/map/parcel-imports/' + id + '/rows', {
            kind,
            kore: kore || undefined,
            per_page: (cursor && cursor.perPage) || undefined,
            after_address: cursor && cursor.afterId ? cursor.afterAddress || '' : undefined,
            after_id: (cursor && cursor.afterId) || undefined,
        }),
    // answers: [{ row_id, kore_lead_id } | { row_id, create_new: true }]
    decideRows: (id, rowIds, decision, fields, answers) =>
        api.post('/map/parcel-imports/' + id + '/decide', {
            row_ids: rowIds,
            decision,
            fields: fields || [],
            answers: answers || [],
        }).then((res) => res.data),
    recheckKore: (id) => api.post('/map/parcel-imports/' + id + '/kore-check').then((res) => res.data),
};

// A property's notes live in iCLIMB; an investor's are its Kore lead's notes too.
const NOTE_PATHS = {
    property: { list: '/properties/', note: '/property-notes/' },
    investor: { list: '/investors/', note: '/investor-notes/' },
};

export const notesApi = {
    list: (owner) => api.get(NOTE_PATHS[owner.type].list + owner.id + '/notes').then((res) => res.data),
    add: (owner, text) => api.post(NOTE_PATHS[owner.type].list + owner.id + '/notes', { text }).then((res) => res.data),
    update: (owner, noteId, text) => api.patch(NOTE_PATHS[owner.type].note + noteId, { text }).then((res) => res.data),
    remove: (owner, noteId) => api.del(NOTE_PATHS[owner.type].note + noteId),
    // Investors only: the list again, after taking in what changed on the Kore lead.
    refresh: (owner) => api.post(NOTE_PATHS[owner.type].list + owner.id + '/notes/refresh').then((res) => res.data),
};

export const propertyResearchedApi = {
    show: (id) => api.get('/properties/' + id + '/researched').then((res) => res.data.researched),
    update: (id, researched) => api.patch('/properties/' + id + '/researched', { researched }).then((res) => res.data.researched),
};

const withKorePending = (res) => ({run: res.data, korePending: ((res.meta || {}).kore_pending) || 0});

// Bulk property upload. `encryption` is the base64 half of the data URL
// FileReader produces, the same shape the previous Node endpoint took.
export const propertyImportApi = {
    start: (fileName, encryption) =>
        api.post('/property-imports', { file_name: fileName, encryption }).then((res) => res.data),
    status: (id) => api.get('/property-imports/' + id).then((res) => res.data),
    // The whole envelope: meta carries the totals, data only the first 500
    // skipped rows.
    skippedRows: (id) => api.get('/property-imports/' + id + '/rows'),
    // The Database tab: edits what it matches, finds owners in Kore.
    startUpdating: (fileName, encryption, dryRun, noteAuthorId) =>
        api.post('/property-imports', {
            file_name: fileName,
            encryption,
            update_existing: true,
            dry_run: dryRun,
            note_author_id: noteAuthorId,
        }).then((res) => res.data),
    // A Database upload that stopped picks up at the slice it stopped on.
    resume: (id) => api.post('/property-imports/' + id + '/resume').then((res) => res.data),
    // The Database tab's newest upload (null when there has been none), with
    // how much Kore work is still queued, so the tab opened again shows it.
    latestUpdating: () => api.get('/property-imports/latest').then(withKorePending),
    updatingStatus: (id) => api.get('/property-imports/' + id).then(withKorePending),
    // Every line of a run, a page at a time, for the downloadable report.
    items: (id, page) => api.get('/process-runs/' + id + '/items', { page }),
};

// The Contacts tab: a file of people, each found in Kore or added to it. The
// same run, slices and report as the Database tab, plus Cancel.
export const contactImportApi = {
    start: (fileName, encryption, dryRun, noteAuthorId) =>
        api.post('/contact-imports', {
            file_name: fileName,
            encryption,
            dry_run: dryRun,
            note_author_id: noteAuthorId,
        }).then((res) => res.data),
    latest: () => api.get('/contact-imports/latest').then(withKorePending),
    status: (id) => api.get('/contact-imports/' + id).then(withKorePending),
    resume: (id) => api.post('/contact-imports/' + id + '/resume').then((res) => res.data),
    cancel: (id) => api.post('/contact-imports/' + id + '/cancel').then(withKorePending),
    rows: (id) => api.get('/contact-imports/' + id + '/rows'),
    items: (id, page) => api.get('/process-runs/' + id + '/items', { page }),
};

// Territory Upload. Same shape as the properties upload above, its own endpoint
// because the rules differ: this one may update the properties it matches.
export const territoryImportApi = {
    start: (fileName, encryption) =>
        api.post('/territory-imports', { file_name: fileName, encryption }).then((res) => res.data),
    status: (id) => api.get('/territory-imports/' + id).then((res) => res.data),
    reviewRows: (id) => api.get('/territory-imports/' + id + '/rows'),
};

// Bulk Edit on the properties list: one set of field values written to every
// ticked property. `attributes` is a PROPERTIES column => value map — an empty
// string clears the column, the same contract a single property save has.
// `currentOwnerId` is the property's owner (an ownership period on the
// Investors tab), which is why it travels beside the column map rather than
// inside it.
export const propertiesApi = {
    bulkUpdate: (propertyIds, attributes, currentOwnerId, referring) =>
        api.post('/properties/bulk-update', {
            property_ids: propertyIds,
            attributes,
            current_owner_id: currentOwnerId || null,
            // REFERRING_OUTSIDE_AGENT columns — a row of their own, not
            // columns on the property.
            referring: referring || {},
        }).then((res) => res.data),
    // Find Dups on the properties list: every live property, grouped by the
    // chosen criteria (a subset of address / apn / legal_desc); `search`
    // keeps only the groups that mention it.
    findDuplicates: (criteria, search, page, propertyIds) =>
        api.post('/properties/find-duplicates', {
            criteria,
            search: search || undefined,
            page: page || undefined,
            property_ids: propertyIds || undefined,
        }).then((res) => res.data),
    // The merge flow behind Find Dups. `compareDuplicates` is step 2's table
    // (primary first); `mergeDuplicates` writes the chosen values onto the
    // primary and merges the rest into it.
    compareDuplicates: (propertyIds) =>
        api.post('/properties/compare-duplicates', { property_ids: propertyIds }).then((res) => res.data),
    mergeDuplicates: (primaryId, mergeIds, choices, selections) =>
        api.post('/properties/merge-duplicates', {
            primary_id: primaryId,
            merge_ids: mergeIds,
            choices,
            // Notes and investors: every property to take rows from.
            selections: selections || undefined,
        }).then((res) => res.data),
    deleteDuplicates: (propertyIds) =>
        api.post('/properties/delete-duplicates', { property_ids: propertyIds }).then((res) => res.data),
};

/**
 * Find Dups and the merge flow for the contact screens. `entity` is one of
 * investors / agents / firms / vendors — the same four the backend route is
 * held to. Unlike the properties list, the whole table is compared: these
 * screens page server-side and have no filtered set to hand over.
 */
export const contactsApi = {
    findDuplicates: (entity, rules, search, page, recordIds) =>
        api.post(`/contacts/${entity}/find-duplicates`, {
            rules,
            search: search || undefined,
            page: page || undefined,
            record_ids: recordIds || undefined,
        }).then((res) => res.data),
    compareDuplicates: (entity, recordIds) =>
        api.post(`/contacts/${entity}/compare-duplicates`, { record_ids: recordIds }).then((res) => res.data),
    mergeDuplicates: (entity, primaryId, mergeIds, choices) =>
        api.post(`/contacts/${entity}/merge-duplicates`, {
            primary_id: primaryId,
            merge_ids: mergeIds,
            choices,
        }).then((res) => res.data),
    deleteDuplicates: (entity, recordIds) =>
        api.post(`/contacts/${entity}/delete-duplicates`, { record_ids: recordIds }).then((res) => res.data),
};